/**
 * **Where the ships are, and the proof that they are not on land.**
 *
 * This module is the answer to the one question the brief asked to be
 * *proved* rather than asserted: a ship must never be ashore, at her spawn or
 * at any point of her patrol. It is answered by construction — a candidate
 * track is accepted only if **every sampled metre of it, and a ring of open
 * water around every sample, is water** — and the same function that decides it
 * is the one the census test re-runs across regions and seeds.
 *
 * ## The architecture decision: how sea geometry reaches the pure sim
 *
 * The water mask is app-side (`apps/web/src/world/water-mask.ts`): it is built
 * out of the region's baked triangles, in draw order, by a topmost-surface test
 * that only the renderer's own model bodies can answer. The sim may not read
 * any of that. Three options were on the table and the third is what this file
 * takes:
 *
 * 1. **Pass the polygon set into the engine.** Rejected: it makes
 *    `packages/engine` depend on a bake format and on `.WLD` draw order, and it
 *    moves several thousand triangles across a boundary whose whole point is
 *    that it is narrow.
 * 2. **Precompute validated patrol areas at bake time.** Rejected: the frame a
 *    patrol lives in is *recentred on the sortie's start aerodrome*, which a
 *    bake cannot know, and a baked area would have to be re-validated anyway the
 *    first time the region's placements changed.
 * 3. **Inject the predicate.** Taken. The engine already has this exact seam and
 *    already uses it for water: `flight/ground.ts`'s `withWater(base, isWater)`
 *    is how the North Sea became a ditching rather than a landing, and
 *    `GroundQuery.surfaceAt` therefore already answers `"water"` at every point
 *    the app's mask says is wet. So the deployment takes a
 *    `(x, z) => boolean`, `waterOf` builds one from the `GroundQuery` the run is
 *    stepped with, and **no new boundary is opened at all**. The sim sees a
 *    function; the app decides what it means; three.js is nowhere in it.
 *
 * The consequence worth stating: a run stepped on `FLAT_GROUND` — every unit
 * test, every fixture, `engine-fingerprint.ts`'s own probe — has no water
 * anywhere, so it deploys nothing. Being dry by default is what makes this
 * module inert everywhere it has not been deliberately switched on.
 *
 * ## Determinism
 *
 * Every draw comes off a `MissionDraw` salted for ships alone, which is a
 * private `RngBank` seeded `fnv1a32(salt, seed)` — the same setup-time
 * generator `flak-placement.ts` uses, and for the same reason: adding this
 * deployment cannot move a single draw any other generator makes. Nothing here
 * touches the encounter's own streams.
 *
 * The *number* of candidates examined depends on the water, so the number of
 * draws does too. That is safe precisely because the draws are on a private
 * bank: a region with more sea does not shift anybody else's dice.
 */

import { fnv1a32 } from "../flight/hash.ts";
import { DEFAULT_SURFACE, type GroundQuery } from "../flight/ground.ts";
import { createRngBankState, RngBank } from "../flight/rng.ts";
import type { EncounterSide } from "./encounter-sides.ts";
import { SHIP_CRUISE_TURN_RADIUS_M, SHIP_LENGTH_OVERALL_M } from "./ship-class.ts";
import { patrolSamples, type ShipPatrol } from "./ship-patrol.ts";

/**
 * Which flag she flies. Named for the **alliance** and not for the player,
 * exactly as the model's two ensign nodes are (ship-design.md §5): *friendly*
 * depends on who the player is flying for, and the flag at the gaff does not.
 */
export type ShipAlliance = "entente" | "central";

/** One deployed ship: an identity, a side, a flag, and the circuit she steams. */
export interface ShipSpec {
  readonly id: string;
  readonly side: EncounterSide;
  readonly alliance: ShipAlliance;
  readonly patrol: ShipPatrol;
}

/** True where the sea is, in the recentred world frame the sim and the scene share. */
export type WaterAt = (x: number, z: number) => boolean;

/** Nothing is wet. The honest answer for a run with no region under it, and the default everywhere. */
export const DRY_WATER_AT: WaterAt = () => false;

/**
 * The water predicate a run's own `GroundQuery` already carries.
 *
 * `fly-region.ts` wraps its region's mask with `withWater`, so `surfaceAt`
 * returns `"water"` at exactly the points the app calls sea. A query with no
 * `surfaceAt` — `FLAT_GROUND`, and every fixture built on it — resolves to
 * `DEFAULT_SURFACE`, which is `"field"`, so it is dry everywhere.
 */
export function waterOf(ground: GroundQuery): WaterAt {
  if (!ground.surfaceAt) return DRY_WATER_AT;
  const surfaceAt = ground.surfaceAt.bind(ground);
  return (x, z) => (surfaceAt(x, z) ?? DEFAULT_SURFACE) === "water";
}

/**
 * Metres of open water a patrol must keep from anything that is not water.
 *
 * Five hundred, and it is three numbers agreeing rather than a round figure:
 * it is **three ship lengths**, it is a little over one and a half turning
 * *diameters* — so a ship that put her helm over at the margin would come round
 * without touching the beach — and at 17 knots it is fifty-seven seconds of
 * sea room. The user's requirement is "never on land"; this is the width of the
 * band in which that is not merely true but not close.
 */
export const SHIP_WATER_MARGIN_M = 500;

/**
 * How finely the circuit is sampled, both to accept it and to prove it.
 *
 * **Half a ship length.** Two consecutive samples therefore overlap along the
 * hull, so there is no gap in the swept path a shoal could hide in; and it is
 * far finer than any feature the region's coastline is drawn with, whose
 * polygons are kilometres.
 */
export const SHIP_TRACK_SAMPLE_M = SHIP_LENGTH_OVERALL_M / 2;

/** Rings of test points around each track sample, as fractions of the margin. */
const MARGIN_RINGS: readonly number[] = [1 / 3, 2 / 3, 1];

/** Test points on each ring. Twelve puts them 30° apart — 262 m of chord at the full margin. */
const RING_POINTS = 12;

/**
 * Nearest and furthest a patrol's centre may be drawn from the sortie's own
 * origin — the town the region is recentred on (`world/region-ground.ts`).
 *
 * **Sixty kilometres is the number the maps chose**, not one picked for feel.
 * Measured over each region's own mask on a 2 km lattice: London's sea is
 * within 6 km of the origin and dense from there out, but **Dunkirk's is
 * effectively absent between 10 and 50 km** — four wet lattice points in that
 * whole band — and only becomes sea proper past 50. An annulus that stopped
 * short of it gave that map no ships at all while its own coast sat just
 * outside the draw, which is a bug that would have read as "the Channel has no
 * navy".
 *
 * Four kilometres inside is the other end of the same argument: a sloop
 * steaming through the circuit the sortie starts in is a ship parked on the
 * player rather than one he finds.
 */
const CENTRE_MIN_M = 4_000;
const CENTRE_MAX_M = 60_000;

/** The straights, metres. Long enough that she is under way rather than circling; short enough to fit a bay. */
const STRAIGHT_MIN_M = 1_500;
const STRAIGHT_MAX_M = 5_000;

/** Metres between two ships' circuit centres, so two escorts are two escorts and not one blur. */
const CENTRE_SEPARATION_M = 6_000;

/** Points drawn looking for a wet centre before the deployment gives up on a region. */
const CENTRE_TRIES = 400;

/** Whole circuits tested. Each one is a few thousand water queries, so this is the real cost bound. */
const TRACK_TRIES = 40;

/** The most ships a sortie will ever carry. */
export const SHIP_MAX_COUNT = 3;

export interface ShipDeploymentOptions {
  /** The mission's own seed. */
  readonly seed: number;
  /** Salt for the private draw, so ship placement cannot move another generator's dice. */
  readonly salt: string;
  readonly isWater: WaterAt;
  readonly side: EncounterSide;
  readonly alliance: ShipAlliance;
  /**
   * An extra veto on where a ship may be, over and above the water test —
   * `mission-spec.ts` uses it to keep an Entente escort out of water behind the
   * enemy's own wire. Defaults to permitting everywhere, which is the right
   * answer for a map with no front line.
   */
  readonly permits?: (x: number, z: number) => boolean;
  /** Upper bound on the draw. Clamped to `SHIP_MAX_COUNT`. */
  readonly maxShips?: number;
  /** Prefix for the ids this call produces, so two deployments in one sortie cannot collide. */
  readonly idPrefix?: string;
}

/** Whether one point has `SHIP_WATER_MARGIN_M` of water all round it. */
function clearWater(isWater: WaterAt, x: number, z: number): boolean {
  if (!isWater(x, z)) return false;
  for (const fraction of MARGIN_RINGS) {
    const radius = SHIP_WATER_MARGIN_M * fraction;
    for (let index = 0; index < RING_POINTS; index += 1) {
      const angle = (2 * Math.PI * index) / RING_POINTS;
      if (!isWater(x + radius * Math.cos(angle), z + radius * Math.sin(angle))) return false;
    }
  }
  return true;
}

/**
 * The whole circuit, every sample of it, in open water and permitted.
 *
 * Exported because it is the *statement* the census test asserts, and a proof
 * that re-implemented its own predicate would be proving something else.
 */
export function patrolIsAtSea(
  patrol: ShipPatrol,
  isWater: WaterAt,
  permits: (x: number, z: number) => boolean = () => true,
): boolean {
  for (const point of patrolSamples(patrol, SHIP_TRACK_SAMPLE_M)) {
    if (!permits(point.x, point.z)) return false;
    if (!clearWater(isWater, point.x, point.z)) return false;
  }
  return true;
}

/** A wet, permitted centre far enough from the ones already taken, or `null` if the region has no room. */
function drawCentre(
  draw: RngBank,
  options: ShipDeploymentOptions,
  taken: readonly ShipPatrol[],
): { readonly x: number; readonly z: number } | null {
  const permits = options.permits ?? (() => true);
  for (let attempt = 0; attempt < CENTRE_TRIES; attempt += 1) {
    const bearing = 2 * Math.PI * draw.nextFloat("mission");
    // Uniform in **area**, not in radius: the annulus's outer rings hold most of
    // its ground, and a uniform radius would spend most of its tries inshore —
    // which on the Channel map is where there is no sea.
    const range = Math.sqrt(
      CENTRE_MIN_M ** 2 + (CENTRE_MAX_M ** 2 - CENTRE_MIN_M ** 2) * draw.nextFloat("mission"),
    );
    const x = range * Math.sin(bearing);
    const z = -range * Math.cos(bearing);
    if (!permits(x, z) || !options.isWater(x, z)) continue;
    const crowded = taken.some(
      (patrol) => Math.hypot(patrol.centreX - x, patrol.centreZ - z) < CENTRE_SEPARATION_M,
    );
    if (!crowded) return { x, z };
  }
  return null;
}

/**
 * Ships for one sortie, or none.
 *
 * **None** is the ordinary answer and not a failure: a region whose water is
 * rivers and duck ponds cannot fit a 500 m margin round a 1 500 m straight, so
 * it gets no ships without any list of regions being written down anywhere.
 * Which maps have a fleet is therefore *read off the map*, the way
 * `scenery-roster.ts` reads which objects are buildings off the game's own
 * collision table, rather than declared here and left to rot.
 */
export function deployShips(options: ShipDeploymentOptions): readonly ShipSpec[] {
  const wanted = Math.min(options.maxShips ?? SHIP_MAX_COUNT, SHIP_MAX_COUNT);
  if (wanted <= 0) return [];
  const draw = new RngBank(createRngBankState(fnv1a32(options.salt, options.seed >>> 0)));
  const permits = options.permits ?? (() => true);
  const patrols: ShipPatrol[] = [];
  const ships: ShipSpec[] = [];

  for (let attempt = 0; attempt < TRACK_TRIES && ships.length < wanted; attempt += 1) {
    const centre = drawCentre(draw, options, patrols);
    if (!centre) break;
    const patrol: ShipPatrol = {
      centreX: centre.x,
      centreZ: centre.z,
      axisRad: 2 * Math.PI * draw.nextFloat("mission"),
      straightM: STRAIGHT_MIN_M + (STRAIGHT_MAX_M - STRAIGHT_MIN_M) * draw.nextFloat("mission"),
      // Her **cruising** turn, not her hard-over one: an escort altering course
      // at the end of a leg puts over a fraction of the helm she would use to
      // avoid a collision, and the first version of this file used the
      // collision figure (user, 2026-09-01: her turns looked too tight). It is
      // twice `SHIP_HARD_TURN_RADIUS_M`, so it is still a turn she could make
      // with room to spare — which is the property the old comment was after.
      radiusM: SHIP_CRUISE_TURN_RADIUS_M,
      phaseM: 0,
    };
    if (!patrolIsAtSea(patrol, options.isWater, permits)) continue;
    ships.push({
      id: `${options.idPrefix ?? "ship"}-${ships.length}`,
      side: options.side,
      alliance: options.alliance,
      // Drawn after acceptance so that where she starts on an accepted circuit
      // cannot change whether the circuit was accepted.
      patrol: { ...patrol, phaseM: draw.nextFloat("mission") * (2 * patrol.straightM) },
    });
    patrols.push(patrol);
  }
  return ships;
}
